import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Mic, Camera, Sparkles, Trophy, MessageCircle, Bell, Zap, ArrowRight, PartyPopper, Flame, Users, ChefHat, Check, ShoppingBag, Star } from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const FEATURE_ICONS = {
  voice: Mic,
  ordering: ChefHat,
  reservations: ShoppingBag,
  party: Trophy,
  chat: MessageCircle,
  notify: Bell,
  boost_reviews: Star,
};

const FEATURE_LABELS = {
  voice: "Menu vocale + foto AI",
  ordering: "Ordini al tavolo",
  reservations: "Asporto & Delivery",
  party: "Menuparty Quiz",
  chat: "Chat live · DM tavoli",
  notify: "Push · Promo a tempo",
  boost_reviews: "Boost Recensioni Google & TripAdvisor",
};

// Extra virtual features attached to specific plans (frontend-only display)
const PLAN_VIRTUAL_FEATURES = {
  plan_all_in: ["boost_reviews"],
};

function PlanCard({ plan, primary, onCta }) {
  const features = plan.features || {};
  const active = Object.entries(features).filter(([, v]) => v).map(([k]) => k);
  const inactive = Object.entries(features).filter(([, v]) => !v).map(([k]) => k);
  const virtual = PLAN_VIRTUAL_FEATURES[plan.id] || [];
  const activeAll = [...active, ...virtual];
  return (
    <div data-testid={`plan-card-${plan.id}`} className={`relative rounded-3xl p-6 sm:p-7 ${primary ? "bg-gradient-to-br from-[#D99B26]/25 via-[#C85A32]/20 to-[#3B503A]/20 border-2 border-[#D99B26] shadow-2xl shadow-[#D99B26]/20" : "bg-white/5 border border-white/10"}`}>
      {primary && <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-[#D99B26] text-[#0B0A08] text-[10px] font-black uppercase tracking-widest shadow">Consigliato</div>}
      <div className="text-xs uppercase tracking-widest font-black text-[#D99B26]">{plan.name}</div>
      {plan.tagline && <div className="text-sm text-white/70 mt-1">{plan.tagline}</div>}
      <div className="mt-3 flex items-baseline gap-1">
        <span className="font-serif-display text-5xl font-black text-white" data-testid={`plan-price-${plan.id}`}>{(plan.price_eur / 30).toFixed(0)}€</span>
        <span className="text-white/60">/giorno</span>
      </div>
      <div className="mt-1 text-[11px] text-white/50">Fatturazione mensile · {Number(plan.price_eur).toFixed(0)}€/mese</div>
      {plan.trial_days > 0 && (
        <div className="mt-1 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#3B503A]/40 text-[#EAF0E9] text-[10px] font-black uppercase tracking-widest">
          <Flame size={10}/> 3 giorni gratis
        </div>
      )}
      <ul className="mt-5 space-y-2 text-sm">
        {activeAll.map((f) => {
          const Icon = FEATURE_ICONS[f] || Sparkles;
          return (
            <li key={f} className="flex gap-2 text-white/95">
              <Icon size={14} className="text-[#D99B26] mt-0.5 flex-shrink-0"/>
              {FEATURE_LABELS[f] || f}
            </li>
          );
        })}
        {inactive.map((f) => (
          <li key={f} className="flex gap-2 text-white/30 line-through">
            <Check size={14} className="text-white/20 mt-0.5 flex-shrink-0"/>
            {FEATURE_LABELS[f]}
          </li>
        ))}
      </ul>
      <Button data-testid={`plan-cta-${plan.id}`} onClick={onCta} className={`mt-6 w-full ${primary ? "bg-gradient-to-r from-[#D99B26] to-[#C85A32] hover:brightness-110" : "bg-white/10 hover:bg-white/20"} text-white rounded-full py-6 font-black border-0`}>
        {plan.trial_days > 0 ? "Prova gratis" : "Attiva ora"} <ArrowRight className="ml-1" size={16}/>
      </Button>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/plans`);
        setPlans(data.plans || []);
      } catch { setPlans([]); }
    })();
  }, []);

  // Order: Menu AI first, All In second (primary)
  const menuAi = plans.find((p) => p.id === "plan_menu_ai") || plans[0];
  const allIn = plans.find((p) => p.id === "plan_all_in") || plans[1];

  return (
    <div className="min-h-screen bg-[#0B0A08] text-[#FBF9F5] overflow-hidden">
      {/* Ambient blobs */}
      <div className="fixed -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#C85A32]/25 blur-[120px] pointer-events-none"/>
      <div className="fixed -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#D99B26]/20 blur-[140px] pointer-events-none"/>
      <div className="fixed top-1/3 left-1/2 w-[400px] h-[400px] rounded-full bg-[#3B503A]/30 blur-[100px] pointer-events-none"/>

      {/* Nav */}
      <header className="relative max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-10 py-5">
        <div className="flex items-center gap-2" data-testid="navbar-brand-logo">
          <span className="font-heading text-xl font-black tracking-tight">VOICE<span className="bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-transparent bg-clip-text">MENU</span></span>
        </div>
        <nav className="flex items-center gap-2 sm:gap-3">
          <Link to="/login" data-testid="nav-login-link" className="text-sm font-semibold text-white/70 hover:text-white">Accedi</Link>
          <Button data-testid="nav-cta-signup" onClick={() => navigate("/signup")} className="bg-gradient-to-r from-[#D99B26] to-[#C85A32] hover:brightness-110 text-white rounded-full px-4 sm:px-5 shadow-lg shadow-[#C85A32]/40 border-0">
            Inizia gratis
          </Button>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-5 sm:px-10 pt-6 pb-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 backdrop-blur border border-white/10 text-[11px] font-bold tracking-widest uppercase text-[#D99B26]">
              <Flame size={12}/> Il menu che i tuoi clienti condividono su TikTok
            </div>
            <h1 className="mt-5 font-serif-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.98] tracking-tight">
              Crea il Menù AI.<br/>
              <span className="italic bg-gradient-to-r from-[#D99B26] via-[#E05328] to-[#C85A32] text-transparent bg-clip-text">Fotografa il vecchio.</span><br/>
              Modificalo a Voce.
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
              Aggiorna il menù <b className="text-white">parlando</b> o <b className="text-white">fotografando</b> il vecchio. Il <b className="text-[#D99B26]">divertimento è assicurato</b>.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-cta-start" onClick={() => navigate("/signup")} className="bg-gradient-to-r from-[#D99B26] to-[#C85A32] hover:brightness-110 text-white rounded-full px-6 py-6 text-base font-bold shadow-xl shadow-[#C85A32]/40 border-0">
                Provalo Gratis <ArrowRight className="ml-2" size={18}/>
              </Button>
              <Button data-testid="hero-cta-demo" variant="outline" onClick={() => navigate("/menu/osteria-del-sole")} className="rounded-full px-6 py-6 text-base bg-white/5 backdrop-blur border-white/20 text-white hover:bg-white/10">
                🎮 Prova il menu demo
              </Button>
            </div>

            <div className="mt-10 flex items-center gap-4 text-xs text-white/60">
              <div className="flex -space-x-2">
                {["#D99B26","#C85A32","#3B503A","#E05328"].map((c, i) => (
                  <div key={i} className="w-8 h-8 rounded-full border-2 border-[#0B0A08]" style={{ background: c }}/>
                ))}
              </div>
              <div>
                <div className="text-white font-bold">3 giorni gratis</div>
                <div>Poi {menuAi ? (Number(menuAi.price_eur) / 30).toFixed(0) : "2"}€/giorno · Nessuna carta richiesta</div>
              </div>
            </div>
          </div>

          {/* Right stack: phone mockup with menu + party */}
          <div className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-[340px] rounded-[38px] border-[6px] border-white/10 bg-black overflow-hidden shadow-2xl shadow-[#C85A32]/20">
              <div className="aspect-[9/19] relative bg-gradient-to-b from-[#161412] to-[#231F1D]">
                <img alt="Menu" src="https://images.unsplash.com/photo-1778850620699-79ba3ff60bf0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHw0fHxmcmVzaCUyMGl0YWxpYW4lMjBwYXN0YSUyMGRpc2glMjBnb3VybWV0JTIwcmVzdGF1cmFudHxlbnwwfHx8fDE3ODg0Njk4NDJ8MA&ixlib=rb-4.1.0&q=85" className="w-full h-1/2 object-cover"/>
                <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest font-black text-white bg-black/50 backdrop-blur px-2 py-1 rounded-full">Osteria del Sole</div>
                  <div className="text-[10px] font-bold text-[#D99B26] bg-white/95 px-2 py-1 rounded-full inline-flex items-center gap-1">
                    <Trophy size={10}/> Quiz LIVE
                  </div>
                </div>
                <div className="absolute inset-x-3 top-1/3 rounded-2xl bg-gradient-to-r from-[#D99B26] to-[#C85A32] p-3 shadow-xl combo-burst">
                  <div className="text-[9px] uppercase font-black text-white/90">🍴 Promo del locale</div>
                  <div className="text-sm font-black text-white">Aperitivo 3€</div>
                  <div className="text-[10px] font-bold text-white/90">⏱ 04:12 rimasti</div>
                </div>
                <div className="absolute inset-x-3 bottom-14 space-y-1.5">
                  <div className="rounded-xl bg-white/95 p-2 flex items-center gap-2">
                    <Trophy size={14} className="text-[#D99B26]"/>
                    <div className="flex-1">
                      <div className="text-[9px] font-bold text-[#3B503A]">COMBO x3! 💥</div>
                      <div className="text-[10px] font-black text-[#231F1D]">+850 punti</div>
                    </div>
                  </div>
                  <div className="rounded-xl bg-white/95 p-2 flex items-center gap-2">
                    <MessageCircle size={14} className="text-[#C85A32]"/>
                    <div className="flex-1">
                      <div className="text-[9px] font-bold text-[#3B503A]">Tavolo 4 · Marco</div>
                      <div className="text-[10px] text-[#231F1D]">"Ordinate le cozze, top!"</div>
                    </div>
                  </div>
                </div>
                <div className="absolute inset-x-3 bottom-3 rounded-xl bg-[#231F1D]/80 backdrop-blur p-2 flex items-center gap-2 border border-white/10">
                  <div className="w-7 h-7 rounded-full bg-[#C85A32] grid place-items-center mic-pulse"><Mic size={12} className="text-white"/></div>
                  <div className="flex-1 text-[10px] font-mono text-white/90">"Aggiungi cozze pepate 12 euro"</div>
                </div>
              </div>
            </div>
            <div className="absolute -left-4 top-10 rounded-full bg-white text-[#231F1D] px-3 py-1.5 text-[11px] font-black shadow-xl flex items-center gap-1 fade-in-up"><Camera size={12}/> Foto → menu</div>
            <div className="absolute -right-4 top-32 rounded-full bg-[#3B503A] text-white px-3 py-1.5 text-[11px] font-black shadow-xl flex items-center gap-1 fade-in-up"><Bell size={12}/> Push ai tavoli</div>
            <div className="absolute -right-4 bottom-24 rounded-full bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-white px-3 py-1.5 text-[11px] font-black shadow-xl flex items-center gap-1 fade-in-up"><PartyPopper size={12}/> Menuparty</div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="relative max-w-6xl mx-auto px-5 sm:px-10 pb-24">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[11px] font-bold tracking-widest uppercase text-[#D99B26]"><Zap size={12}/> Tutto in una app</div>
          <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl font-black">Il menu è solo l'inizio</h2>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: Mic, title: "Voce", desc: "Parla al mic per aggiungere, aggiornare o cancellare piatti. Zero tastiera.", color: "from-[#D99B26] to-[#C85A32]" },
            { icon: Camera, title: "Foto → Menu", desc: "Fotografa un menu cartaceo. L'AI estrae piatti, prezzi, categorie e allergeni.", color: "from-[#3B503A] to-[#2f4030]" },
            { icon: Flame, title: "Menu del Giorno", desc: "Sezione parallela indipendente per fuori menu e specialità del giorno. Zero conflitti con il menu fisso.", color: "from-[#E05328] to-[#D99B26]" },
            { icon: Sparkles, title: "Foto AI dei piatti", desc: "Generazione automatica di immagini gourmet per ogni piatto (rigenerabili).", color: "from-[#C85A32] to-[#E05328]" },
            { icon: Trophy, title: "Gaming", desc: "Torneo con streak, combo, confetti, badge e premi coupon. Ingaggio virale.", color: "from-[#D99B26] to-[#E05328]" },
            { icon: MessageCircle, title: "Chat live + DM", desc: "Chat pubblica del locale, DM tra tavoli, staff che scrive con badge dorato.", color: "from-[#3B503A] to-[#C85A32]" },
            { icon: Bell, title: "Push del locale", desc: "Lancia promo a tempo con countdown, prenotabili in tap. Notifiche push in tempo reale.", color: "from-[#C85A32] to-[#D99B26]" },
            { icon: Star, title: "Boost Recensioni", desc: "Il link recensione (Google, TripAdvisor…) parte in chat e appare a fine quiz. Anche chi non vince lascia una recensione.", color: "from-[#D99B26] to-[#3B503A]" },
          ].map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} data-testid={`feature-${i}`} className="rounded-3xl bg-white/5 backdrop-blur border border-white/10 p-5 hover:bg-white/10 hover:border-white/20 transition group">
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${f.color} grid place-items-center shadow-lg group-hover:scale-110 transition`}><Icon size={20} className="text-white"/></div>
                <h3 className="mt-4 font-heading text-lg font-black">{f.title}</h3>
                <p className="mt-1.5 text-sm text-white/70 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Social/Gaming banner */}
      <section className="relative max-w-6xl mx-auto px-5 sm:px-10 pb-24">
        <div className="relative rounded-[32px] bg-gradient-to-br from-[#D99B26] via-[#E05328] to-[#C85A32] p-8 sm:p-12 overflow-hidden">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)", backgroundSize: "12px 12px" }}/>
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/30 text-white text-[10px] font-black uppercase tracking-widest"><PartyPopper size={11}/> Menuparty Effect</div>
              <h3 className="mt-4 font-serif-display text-3xl sm:text-4xl font-black text-white leading-tight">I clienti tornano.</h3>
              <p className="mt-3 text-white/95 leading-relaxed">Ogni tavolo diventa un experience. Le persone ordinano, giocano nell'attesa. Tu ottieni contatti, scontrini più alti con le promo istantanee e molte più recensioni automatiche.</p>
              <div className="mt-5 grid grid-cols-3 gap-3">
                {[{v:"+34%",l:"tempo al tavolo"},{v:"+18%",l:"scontrino medio"},{v:"5x",l:"condivisioni social"}].map((s,i)=>(
                  <div key={i} className="rounded-2xl bg-black/25 backdrop-blur p-3 text-center">
                    <div className="font-serif-display text-2xl font-black text-white">{s.v}</div>
                    <div className="text-[10px] uppercase tracking-wider text-white/90 font-bold">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-3xl bg-[#161412] p-5 shadow-2xl">
                <div className="text-[10px] uppercase tracking-widest font-black text-[#D99B26]">Classifica live</div>
                <div className="mt-3 space-y-2">
                  {[["Luca T3", 4820, "🥇"], ["Sara T7", 4550, "🥈"], ["Marco T2", 3980, "🥉"], ["Team T5", 3720, "4"]].map(([n,p,e],i)=>(
                    <div key={i} className="flex items-center gap-3 rounded-xl bg-white/5 p-2.5">
                      <div className="text-lg">{e}</div>
                      <div className="flex-1 font-bold text-white">{n}</div>
                      <div className="font-mono text-[#D99B26] font-black">{p}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute -top-3 -right-3 rounded-full bg-white text-[#C85A32] px-3 py-1.5 text-[11px] font-black shadow-xl inline-flex items-center gap-1 wobble">🔥 COMBO x5!</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="relative max-w-4xl mx-auto px-5 sm:px-10 pb-24">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[11px] font-bold tracking-widest uppercase text-[#D99B26]"><Zap size={12}/> Prezzo semplice</div>
          <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl font-black">Un caffè al giorno</h2>
          <p className="mt-3 text-white/70">Il super admin attiva il tuo account con il piano scelto · funzioni gestite dal backoffice.</p>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {menuAi && <PlanCard plan={menuAi} primary={false} onCta={() => navigate("/signup")}/>}
          {allIn && <PlanCard plan={allIn} primary={true} onCta={() => navigate("/signup")}/>}
          {plans.length === 0 && (
            <>
              <div className="rounded-3xl bg-white/5 border border-white/10 p-6 animate-pulse h-64"/>
              <div className="rounded-3xl bg-white/5 border border-white/10 p-6 animate-pulse h-64"/>
            </>
          )}
        </div>
      </section>

      <footer className="relative border-t border-white/10 py-6 text-center text-xs text-white/50">
        Italiabrain · P.IVA 06085250873
      </footer>
    </div>
  );
}
